import type { ReactNode } from 'react';
import { K } from '../kabinet/atoms';
import { IconSearch } from './icons';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПУСТОТА И ОТКАЗ — ОДИН РИСУНОК НА ВЕСЬ КАБИНЕТ (ответ 32 от 23.08).
 *
 * «Пустая таблица — блок по центру: знак, крупная строка „чего нет“,
 * строка причины, одна кнопка выхода из положения. Тот же рисунок
 * для любой пустой таблицы, меняются только слова».
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЧТО БЫЛО ДО ЭТОГО ФАЙЛА.
 *
 * Девять мест кабинета рисовали пустоту одним абзацем `rk-empty`:
 * серый текст по центру, без знака, без крупной строки и без кнопки.
 * Абзац читается как примечание — глаз проходит мимо него к таблице,
 * которой нет. Хуже другое: у половины этих абзацев не было выхода.
 * «По запросу „серьги“ ничего не нашлось» — и всё; чтобы вернуться
 * к списку, надо догадаться стереть запрос.
 *
 * КНОПКА ЗДЕСЬ НЕ УКРАШЕНИЕ, А СМЫСЛ ВСЕГО БЛОКА. Пустота — это
 * тупик, и рисунок обязан показывать из него дверь: «Показать все»,
 * «Обновить заказы», «Подключить кабинет». Если двери нет вовсе
 * (данные появятся сами), кнопки нет — и тогда причина обязана
 * объяснить, чего ждать и сколько.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПОЧЕМУ ОТКАЗ — ОТДЕЛЬНЫЙ ВИД, А НЕ ТА ЖЕ ПУСТОТА ДРУГИМИ СЛОВАМИ.
 *
 * Пустота выровнена по центру и говорит «ничего нет». Отказ прижат
 * влево, начинается с красной метки и говорит «не прочиталось».
 * Разница не косметическая: человеку, у которого упала выгрузка,
 * «кампаний нет» — это ложь, от которой он пойдёт искать пропавшие
 * кампании в Wildberries.
 *
 * Оба вида нарисованы частицами эталона (`k-пусто`, `k-отказ` и то,
 * что внутри). Частицы вырезаны из состояний `data:'empty'`
 * и `data:'error'` — их снимает `probe/design-html.mjs`, потому что
 * меню до них не доводит: они приходят из данных.
 * ═══════════════════════════════════════════════════════════════════════
 */

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ТРИ ВИДА ПУСТОТЫ — ПОТОМУ ЧТО ИХ ТРИ В ЭТАЛОНЕ, А НЕ ПОТОМУ ЧТО
 * ТАК ЗАХОТЕЛОСЬ.
 *
 * Ответ 32 говорит «тот же рисунок для любой пустой таблицы, меняются
 * только слова», и рисунок действительно один. Но числа у трёх экранов
 * эталона разные, и разница замеряется:
 *
 *   поиск   `состояние-ничего-не-нашлось`  текст `--ink-2`, причина 44ch
 *   группа  `состояние-группа-пуста`       текст по наследству,     46ch
 *   ключ    `состояние-нет-ключа`          то же,                   52ch,
 *           крупная строка кеглем h2, кнопка ГЛАВНАЯ (цвет марки)
 *           и пятая строка-подсказка под ней
 *
 * СВЕСТИ ИХ К ОДНОМУ ЗНАЧИЛО БЫ РЕШИТЬ ЗА ДИЗАЙНЕРА. Числа взяты
 * из его файла частицами, вопрос (К-133) — «так задумано или
 * расхождение?» — ушёл ему. Придёт ответ «одинаково» — здесь останется
 * один вид, и правка будет в одну строку.
 * ═══════════════════════════════════════════════════════════════════════
 */
export type ВидПустоты = 'поиск' | 'группа' | 'ключ';

const ВИДЫ: Record<ВидПустоты, { блок: string; причина: string; заголовок?: string }> = {
  поиск: { блок: K.пусто, причина: K.причинаПусто },
  группа: { блок: K.пустоГруппы, причина: K.причинаПустойГруппы },
  ключ: { блок: K.пустоБезКлюча, причина: K.причинаБезКлюча, заголовок: K.заголовокБезКлюча },
};

export interface ПустоProps {
  /**
   * Крупная строка: ЧЕГО НЕТ. Не «нет данных», а что именно
   * не нашлось и по какому условию.
   */
  что: ReactNode;
  /**
   * Строка причины. ОБЯЗАТЕЛЬНА: «нет данных» без причины читается
   * как поломка сервиса (ответ 32).
   */
  почему: ReactNode;
  /** Знак над строкой. По умолчанию лупа — как в эталоне у поиска. */
  знак?: ReactNode;
  /**
   * Одна кнопка выхода из положения. Нет выхода — нет кнопки.
   *
   * `главная` — цвет марки вместо тихой рамки. Так в эталоне ровно
   * у одной пустоты: «Подключить кабинет» это не «показать все»,
   * а единственный вход в сервис, и выглядеть он должен главным
   * действием экрана.
   */
  кнопка?: { текст: string; жать: () => void; главная?: boolean; имя?: string };
  /** Имя узла для сверки с эталоном, если это место размечено. */
  имя?: string;
  /** Какой из трёх видов эталона. По умолчанию тот, что у поиска. */
  вид?: ВидПустоты;
  /** Строка под кнопкой. Есть только у «нет ключа»: куда идти за ключом. */
  подпись?: ReactNode;
}

/**
 * ЗНАК РИСУЕТСЯ 30×30 ШТРИХОМ 1.2 — так в эталоне. Набор значков
 * кабинета рисует штрихом 1.6: на 16 точках это верно, а на тридцати
 * даёт жирную лупу, которая спорит с текстом за внимание.
 */
export function ЗнакПустоты({ children }: { children?: ReactNode }) {
  return <span className={K.знакПусто}>{children ?? <IconSearch size={30} штрих={1.2} />}</span>;
}

export function Пусто({ что, почему, знак, кнопка, имя, вид = 'поиск', подпись }: ПустоProps) {
  const н = ВИДЫ[вид];
  return (
    <div className={н.блок} data-d={имя}>
      <ЗнакПустоты>{знак}</ЗнакПустоты>
      {/* Крупная строка — голый `b` у двух видов из трёх: жирность даёт
          тег, размер наследуется. Кегль своим объявлением стоит только
          у «нет ключа» — там это заголовок экрана, а не строка списка. */}
      <b className={н.заголовок}>{что}</b>
      <span className={н.причина}>{почему}</span>
      {кнопка !== undefined && (
        <button
          type="button"
          data-d={кнопка.имя}
          className={кнопка.главная === true ? K.кнопкаПодключитьКабинет : K.кнопкаПусто}
          onClick={кнопка.жать}
        >
          {кнопка.текст}
        </button>
      )}
      {подпись !== undefined && <span className={K.подсказкаБезКлюча}>{подпись}</span>}
    </div>
  );
}

export interface ОтказProps {
  /** Крупная строка: что именно не прочиталось. */
  что: ReactNode;
  /**
   * Причина. Здесь она отвечает на главный вопрос человека: «мои
   * деньги и настройки целы?» — и отвечать на него надо прямо.
   */
  почему: ReactNode;
  /** Повторить. Нечего повторять — кнопки нет. */
  кнопка?: { текст: string; жать: () => void };
  имя?: string;
}

export function Отказ({ что, почему, кнопка, имя }: ОтказProps) {
  return (
    <div className={K.отказ} data-d={имя}>
      <span className={K.меткаОтказа}>отказ</span>
      <b className={K.заголовокОтказа}>{что}</b>
      <span className={K.причинаОтказа}>{почему}</span>
      {кнопка !== undefined && (
        <button type="button" className={K.кнопкаОтказа} onClick={кнопка.жать}>
          {кнопка.текст}
        </button>
      )}
    </div>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПУСТАЯ КЛЕТКА — ПРОЧЕРК С ПРИЧИНОЙ (ответ 32).
 *
 * «Прочерк „—“ цветом `--ink-3` и подсказка-причина в `title`. Причина
 * обязательна: „нет данных“ без причины читается как ошибка сервиса».
 *
 * ПРИЧИНА — ОБЯЗАТЕЛЬНЫЙ ДОВОД, А НЕ УКРАШЕНИЕ. Прочерк отвечает
 * «нечего показать», и на этом человек останавливается с вопросом
 * «почему». Без ответа он идёт искать поломку — в поддержку, в свой
 * кабинет Wildberries, в ключ. Ровно один из этих походов дороже,
 * чем написать здесь строку.
 *
 * ТИП ТРЕБУЕТ ЕЁ, А НЕ ПРОСИТ. Необязательный `почему?` означал бы,
 * что первый же новый столбец покажет голый прочерк и никто
 * не заметит.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function Прочерк({
  почему,
  словом,
  цвет,
  жирно = false,
}: {
  почему: string;
  словом?: string;
  /**
   * ═══════════════════════════════════════════════════════════════════
   * ПРОЧЕРК, КОТОРЫЙ САМ И ЕСТЬ ПРИГОВОР, — ЦВЕТОМ ВЕРДИКТА.
   *
   * Ровно одно место: «Заказ, ₽» в таблице запросов. Эталон,
   * `cellMap.cpo`: `[cpoT, dim || vFg, dim ? '400' : '600']`, где
   * `cpoT` при нуле заказов и есть черта. То есть черта красится
   * ТЕМ ЖЕ правилом, что число на её месте, — красным и полужирным.
   *
   * ПОЧЕМУ ЭТО НЕ ЛАЗЕЙКА В СТРОГОЙ ЗАГОТОВКЕ. В остальных графах
   * черта означает «посчитать не из чего» — это сообщение о данных.
   * Здесь она означает «902 ₽ и ни одного заказа»: не отсутствие
   * данных, а сам результат, и притом худший из возможных. Серая
   * черта прячет его ровно в той графе, ради которой на строку
   * и смотрят.
   *
   * Причина при этом остаётся обязательной — тип её требует, и цвет
   * её не заменяет: красное «—» без подсказки объясняет не больше,
   * чем серое.
   * ═══════════════════════════════════════════════════════════════════
   */
  цвет?: string;
  жирно?: boolean;
}) {
  /**
   * СЛОВАМИ ВМЕСТО ЧЕРТЫ — ТАМ, ГДЕ ПУСТО ВСЕГДА И ПО ОДНОЙ ПРИЧИНЕ.
   *
   * Единственное такое место — столбец «Выручка» в таблице запросов:
   * выручку ПО ЗАПРОСУ Wildberries не отдаёт ни в одном отчёте, и черта
   * там стоит во всех трёхстах строках. Триста черт подряд читаются
   * как «выгрузка сломалась», а «нет у WB» — как ответ. Так и в эталоне.
   *
   * Причина остаётся обязательной: слово короткое, подробность
   * по наведению.
   */
  return (
    <span
      className="rk-faint"
      title={почему}
      /* Пусто, пока не позвали с цветом: тогда работает `rk-faint`,
         и обычный прочерк остаётся ровно таким, каким был. */
      style={цвет === undefined && !жирно ? undefined : { color: цвет, fontWeight: жирно ? 600 : undefined }}
    >
      {словом ?? '—'}
    </span>
  );
}
